import { useMemo } from "react";

import Link from "next/link";
import { useRouter } from "next/router";

import { Menu, Button } from "antd";

import { useAuthenticator } from "@aws-amplify/ui-react";

import { AnimatePresence, motion } from "framer-motion";

import Avatar from "@/components/Avatar";

import {
  HiBadgeCheck,
  HiCalendar,
  HiClipboard,
  HiClipboardList,
  HiOutlineChartPie,
  HiOutlineCog,
  HiOutlineFlag,
  HiOutlinePresentationChartLine,
  HiOutlinePuzzle,
  HiUserGroup,
  HiUsers,
  HiOutlineLogout,
  HiHome,
} from "react-icons/hi";

import { useTokenData } from "@/hooks/useTokenData";
import useAuthStore from "@/store/useAuthStore";
import useLayoutStore from "@/store/useLayoutStore";

import { PAGE_URL } from "@/utils/constants";
import { useTypeTier } from "../Card/card-user-profile";
import { QuestionCircleOutlined } from "@ant-design/icons";
import useWindowResize, { useWindowResizeTablet } from "@/hooks/useWindowResize";
import useDashboardName from "@/hooks/useDashboardName";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useIsViewAsHR } from "@/hooks/useIsViewAsHR";

export default function SidebarMobile() {
  const { track } = useAnalytics();
  const collapsed = useLayoutStore.useCollapsed();
  const setCollapsed = useLayoutStore.useSetCollapsed();

  const authUser = useAuthStore.useUser();
  const handlerLogout = useAuthStore.useLogout();

  const router = useRouter();
  const { isHR } = useIsViewAsHR();

  const tokenData = useTokenData();

  const pathName = router.pathname.split("/");
  const dashboardName = useDashboardName();

  const { signOut } = useAuthenticator((context) => [context.signOut]);

  const logout = (): void => {
    handlerLogout();
    signOut();
    setCollapsed(false);
    router.push({ pathname: PAGE_URL.login });
  };

  const pathKeys: string = useMemo(() => {
    if (pathName[1] === "hr") {
      if (pathName[2] === "reports") {
        return pathName[3];
      }

      return pathName[2];
    }

    return pathName[1];
  }, [pathName]);

  const type_tier = useTypeTier(authUser?.bio?.user?.points);

  const isMobile = useWindowResize();
  const isTablet = useWindowResizeTablet();

  if(!isMobile && !isTablet) return null;

  return (
    <AnimatePresence>
      {collapsed && (
        <motion.div
          key="sidebar-mobile"
          initial={{
            opacity: 0,
            clipPath: "polygon(0 0, 100% 0, 100% 0, 0 0)",
          }}
          animate={{
            opacity: 1,
            clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
          }}
          exit={{ opacity: 0, clipPath: "polygon(0 0, 100% 0, 100% 0, 0 0)" }}
          transition={{ duration: 0.8, type: "spring" }}
          className="fixed top-16 left-0 right-0 bottom-0 z-[1030] bg-black h-screen overflow-y-auto pb-[200px] lg:pb-0"
        >
          <Menu
            // theme="light"
            mode="inline"
            onClick={() => setCollapsed(false)}
            defaultSelectedKeys={
              pathKeys === "" ? ["menu-home"] : [`menu-${pathKeys}`]
            }
            items={[
              {
                key: "menu-dashboard",
                icon: <HiHome />,
                label: <p className="flex items-center gap-2">My Dashboard</p>,
                onClick: () => {
                  track("Left NavBar", "Click", `My Dashboard Menu`)
                  router.push(PAGE_URL.dashboard)
                },              },
              {
                key: "menu-connections",
                icon: <HiUsers />,
                label: (
                  <span id={isMobile ? "sidebar-my-connections" : ""}>
                    My Connections
                  </span>
                ),
                onClick: () => {
                  track("Left NavBar", "Click", `My Connections Menu`)
                  router.push(PAGE_URL.connections)
                },              
              },
              // {
              //   key: "menu-notification",
              //   icon: <HiClipboardList />,
              //   label: "My Notifications",
              //   onClick: () => router.push(PAGE_URL.notification),
              // },
              // {
              //   key: "menu-circles",
              //   icon: <HiUserGroup />,
              //   label: "My Circles",
              //   onClick: () => router.push(PAGE_URL.circles),
              // },
              // {
              //   key: "menu-events",
              //   icon: <HiCalendar />,
              //   label: "My Events",
              //   onClick: () => router.push(PAGE_URL.events),
              // },
              {
                key: "menu-mycalendar",
                icon: <HiCalendar />,
                label: "My Community",
                onClick: () => {
                  track("Left NavBar", "Click", `My Community Menu`)
                  router.push(PAGE_URL.mycalendar)
                },              },
              {
                key: "menu-goals",
                icon: <HiClipboard />,
                label: "My Goals",
                onClick: () => {
                  track("Left NavBar", "Click", `My Goals Menu`)
                  router.push(PAGE_URL.goals)
                },              
              },
              {
                key: "menu-rewards",
                icon: <HiBadgeCheck />,
                label: "My Rewards",
                onClick: () => {
                  track("Left NavBar", "Click", `My Rewards Menu`)
                  router.push(PAGE_URL.rewards)
                },              
              },
              // {
              //   key: "menu-inbox",
              //   icon: <HiMailOpen />,
              //   label: "My Inbox",
              //   onClick: () => router.push(PAGE_URL.inbox),
              // },
            ]}
            className="mobile"
          />

          <div className=" py-1">
            <hr className="bg-neutral-800" />
          </div>

          <Menu
            // theme="light"
            mode="inline"
            onClick={() => setCollapsed(false)}
            defaultSelectedKeys={
              pathKeys === "" ? ["menu-home"] : [`menu-${pathKeys}`]
            }
            items={[
              {
                key: "menu-how-to-use-myconnect",
                icon: <QuestionCircleOutlined className="text-white" />,
                label: "How to use MyConnect.ai",
                children: [
                  {
                    key: "menu-how-to-use-myconnect-global-search",
                    label: (
                      <Link
                        href={{
                          pathname: router.pathname,
                          query: {
                            ...router.query,
                            userguidevideo: "global-search",
                          },
                        }}
                        shallow
                        onClick={() => track("Left NavBar", "Click", `Global Search`)}
                      >
                        <p>Global Search</p>
                      </Link>
                    ),
                  },
                  {
                    key: "menu-how-to-use-myconnect-invite-champions",
                    label: (
                      <Link
                        href={{
                          pathname: router.pathname,
                          query: {
                            ...router.query,
                            userguidevideo: "invite-champions",
                          },
                        }}
                        shallow
                        onClick={() => track("Left NavBar", "Click", `Invite Champions`)}
                      >
                        <p>Invite Champions</p>
                      </Link>
                    ),
                  },
                  {
                    key: "menu-how-to-use-myconnect-make-connections",
                    label: (
                      <Link
                        href={{
                          pathname: router.pathname,
                          query: {
                            ...router.query,
                            userguidevideo: "make-connections",
                          },
                        }}
                        shallow
                        onClick={() => track("Left NavBar", "Click", `Make Connections`)}
                      >
                        <p>Make Connections</p>
                      </Link>
                    ),
                  },
                  ...(tokenData?.isPremium === "true"
                    ? [
                        {
                          key: "menu-how-to-use-myconnect-manage-your-goals",
                          label: (
                            <Link
                              href={{
                                pathname: router.pathname,
                                query: {
                                  ...router.query,
                                  userguidevideo: "manage-your-goals",
                                },
                              }}
                              shallow
                              onClick={() => track("Left NavBar", "Click", `Manage your Goals`)}
                            >
                              <p>Manage your Goals</p>
                            </Link>
                          ),
                        },
                      ]
                    : []),
                  {
                    key: "menu-how-to-use-myconnect-complete-profile",
                    label: (
                      <Link
                        href={{
                          pathname: router.pathname,
                          query: {
                            ...router.query,
                            userguidevideo: "complete-profile",
                          },
                        }}
                        shallow
                        onClick={() => track("Left NavBar", "Click", `Complete your Profile`)}
                      >
                        <p>Complete your Profile</p>
                      </Link>
                    ),
                  },
                  {
                    key: "menu-how-to-use-myconnect-create-mentorship",
                    label: (
                      <Link
                        href={{
                          pathname: router.pathname,
                          query: {
                            ...router.query,
                            userguidevideo: "create-mentorship",
                          },
                        }}
                        shallow
                        onClick={() => track("Left NavBar", "Click", `Create Mentorships`)}
                      >
                        <p>Create Mentorships</p>
                      </Link>
                    ),
                  },
                  {
                    key: "menu-how-to-use-myconnect-set-goals",
                    label: (
                      <Link
                        href={{
                          pathname: router.pathname,
                          query: {
                            ...router.query,
                            userguidevideo: "set-goals",
                          },
                        }}
                        shallow
                        onClick={() => track("Left NavBar", "Click", `Set Goals`)}
                      >
                        <p>Set Goals</p>
                      </Link>
                    ),
                  },
                  ...(tokenData?.isPremium === "true"
                    ? [
                        {
                          key: "menu-how-to-use-myconnect-focused-milestone",
                          label: (
                            <Link
                              href={{
                                pathname: router.pathname,
                                query: {
                                  ...router.query,
                                  userguidevideo: "focused-milestone",
                                },
                              }}
                              shallow
                              onClick={() => track("Left NavBar", "Click", `Focused Milestone`)}
                            >
                              <p>Focused Milestone</p>
                            </Link>
                          ),
                        },
                      ]
                    : []),
                ],
              },
              // {
              //   key: "menu-inbox",
              //   icon: <HiMailOpen />,
              //   label: "My Inbox",
              //   onClick: () => router.push(PAGE_URL.inbox),
              // },
            ]}
            className="mobile"
          />

          <div className="py-1">
            <hr className="bg-neutral-800" />
          </div>

          {isHR && (
            <div>
              <Menu
                // theme="dark"
                mode="inline"
                onClick={() => setCollapsed(false)}
                defaultSelectedKeys={[`menu-${pathKeys}`]}
                items={[
                  {
                    key: "menu-hr",
                    icon: <HiOutlinePresentationChartLine />,
                    label: dashboardName,
                    onClick: () => {
                      track("Left NavBar", "Click", `Insight Dashboard`)
                      router.push(PAGE_URL.hr)
                    },                  },
                  {
                    key: "menu-reports",
                    icon: <HiOutlineChartPie />,
                    label: "Reports",

                    children: [
                      {
                        key: "menu-mentorship",
                        label: "Mentorship",
                        onClick: () => {
                          track("Left NavBar", "Click", "Mentorship")
                          router.push(PAGE_URL.reportsMentorship)
                        },                      },
                      {
                        key: "menu-connections",
                        label: "Connections",
                        onClick: () => {
                          track("Left NavBar", "Click", "Connections")
                          router.push(PAGE_URL.reportsConnections)
                        },                      },
                      // {/* remove this item for non premium users */}
                      ...(tokenData?.isPremium === "true"
                        ? [
                            {
                              key: "menu-milestones",
                              label: "Milestones",
                              onClick: () => {
                                  track("Left NavBar", "Click", "Milestones")
                                  router.push(PAGE_URL.reportsMilestones)
                              },
                            },
                          ]
                        : []),
                      {
                        key: "menu-report-rewards",
                        label: "Rewards",
                        onClick: () => {
                          track("Left NavBar", "Click", "Rewards")
                          router.push(PAGE_URL.reportsRewards)
                        },                      
                      },
                    ],
                  },
                  {
                    key: "menu-org-settings",
                    icon: <HiOutlineCog />,
                    label: "Organization Settings",
                    onClick: () => {
                      track("Left NavBar", "Click", "Organization Settings")
                      router.push(PAGE_URL.orgSettings)
                    },                  },
                  {
                    key: "menu-s",
                    icon: <HiOutlinePuzzle />,
                    label: "Manage Content",
                    onClick: () => {
                      track("Left NavBar", "Click", "Manage Content")
                      router.push(PAGE_URL.manageContent)
                    },                  },
                  {
                    key: "menu-red-flag",
                    icon: <HiOutlineFlag />,
                    label: "Red Flags Inbox",
                    onClick: () => {
                      track("Left NavBar", "Click", "Red Flags Inbox")
                      router.push(PAGE_URL.redFlag)
                    },                  },
                ]}
                className="mobile"
              />
            </div>
          )}

          <div className="flex w-full gap-4 justify-between items-center px-4 fixed bottom-0 py-4 bg-card">
            <div
              className="flex items-center gap-3  w-full"
              id="header-avatar-element"
            >
              <Avatar
                size="sm"
                alt="profile"
                type_tier={type_tier as never}
                src={authUser?.bio?.user?.avatarUrl}
                name={authUser?.bio?.user?.firstName}
              />
              <div className="flex flex-col">
                <span className="text-base font-bold">
                  {authUser?.bio?.user?.firstName}{" "}
                  {authUser?.bio?.user?.lastName}
                </span>
                <Link
                  href={PAGE_URL.profile}
                  onClick={() => setCollapsed(false)}
                >
                  <span className="text-primary text-sm font-bold">
                    View Profile
                  </span>
                </Link>
              </div>
            </div>
            <div>
              <Button
                type="text"
                size="large"
                icon={<HiOutlineLogout className="text-red-400 text-xl" />}
                onClick={() => logout()}
              ></Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
